/**
 * Returns a shuffled copy of `choices` (Fisher-Yates), leaving the input untouched.
 *
 * Callers shuffle once per mount, not per render, so options never move under a
 * respondent's finger. `random` is injectable so tests can pin the order.
 */
export function shuffleChoices(choices: string[], random: () => number = Math.random): string[] {
  const shuffled = [...choices];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));

    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }

  return shuffled;
}

/**
 * Keeps a shuffled order across schema refetches and remounts: `previous` is
 * returned as long as it holds exactly the current choices, and a fresh
 * shuffle is made only when the author actually changed them.
 */
export function stableChoiceOrder(
  previous: string[] | undefined,
  choices: string[],
  random: () => number = Math.random
): string[] {
  const unchanged =
    previous !== undefined &&
    previous.length === choices.length &&
    choices.every((choice) => previous.includes(choice));

  return unchanged ? previous : shuffleChoices(choices, random);
}
